import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { normalize } from 'normalizr';
import moment from 'moment';
import * as d3 from 'd3';
import { Button, Icon } from 'semantic-ui-react';

import { translate } from 'react-i18next';
import { get, update } from '../../utils/communications';
import { apiEndpoint } from '../../utils/env';
import { localSet } from '../../utils/localStorage';
import toFinish from '../../utils/toFinish';
import activeAlarmsSchema from '../../normalizr-schemas/activeAlarmsSchema';
import alarmsConfigSchema from '../../normalizr-schemas/alarmsConfigSchema';
import AppInfo from '../reusable/AppInfo';

class FloorPlan extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    imageUrl: PropTypes.string.isRequired,
    currentOrganisation: PropTypes.string.isRequired,
    hall: PropTypes.shape({}).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      edit: false,
      selected: [],
      saveIsPending: false,
      moveToMetrics: false,
    };
    this.modifyEdit = this.modifyEdit.bind(this);
    this.unselectAll = this.unselectAll.bind(this);
    this.openMetrics = this.openMetrics.bind(this);
  }

  async componentDidMount() {
    const { currentOrganisation, hall } = this.props;
    const that = this;

    const [machinesErr, machinesResp] = await toFinish(get(
      `${apiEndpoint}/organisations/${currentOrganisation}/machines`,
      null,
      false,
      { hall: hall.name },
    ));

    const machines = machinesResp.data.attributes.entities;

    let positions;
    if (hall.thingsLocation) {
      positions = hall.thingsLocation;
    } else {
      positions = {};
      machines.forEach((machine) => {
        positions[machine.shortName] = {
          x: 0,
          y: 20,
        };
      });
    }

    // const machinesWithAlarm = [];
    // const machinesWithPosition = Object.keys(positions);
    // const machinesWithoutPosition = [];
    // const machinesId = [];

    // machines.forEach((machine) => {
    //   if (machine.currentState !== 'green') {
    //     machinesWithAlarm.push(machine.id);
    //   }
    //   if (!machinesWithPosition.includes(machine.shortName)) {
    //     machinesWithoutPosition.push(machine);
    //   }
    //   machinesId.push(machine.id);
    // });

    const [alarmsErr, alarmsRes] = await toFinish(get(`${apiEndpoint}/organisations/${currentOrganisation}/alarms`, null, false, {
      hall: hall.name,
      status: 'active',
    }));

    const { entities } = alarmsRes.data.attributes;

    const normalizedAlarms = normalize(entities, activeAlarmsSchema);
    const alarms = {};
    alarms.allIds = normalizedAlarms.result;
    alarms.byId = normalizedAlarms.entities.activeAlarms;

    const [alarmsCOnfigErr, alarmsConfigRes] = await toFinish(get(`${apiEndpoint}/organisations/${currentOrganisation}/alarms-config`, null, false, {
      hall: hall.name,
    }));

    const { entities: alarmsConfiguration } = alarmsConfigRes.data.attributes;
    const normalizedAlarmsConfig = normalize(alarmsConfiguration, alarmsConfigSchema);
    const alarmsConfig = {};
    alarmsConfig.allIds = normalizedAlarmsConfig.result;
    alarmsConfig.byId = normalizedAlarmsConfig.entities.alarmsConfig;

    const timeNow = moment();

    const { viewBoxWidth, viewBoxHeight } = hall;
    const rectWidth = 40;
    const rectHeight = 40;
    const textMarginX = 20;
    const textMarginY = 25;
    const circleMarginX = 40;
    const circleMarginY = 0;
    const circleTextMarginX = 36;
    const circleTextMarginY = 4;

    const svg = d3.select('#floorPlan')
      .append('div')
      .append('svg')
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .attr('viewBox', `0 0 ${viewBoxWidth} ${viewBoxHeight}`)
      .classed('svg-content-responsive', true);
    // .call(d3.drag()
    //   .on('start', function dragStart() {
    //     d3.select(this)
    //       .append('rect')
    //       .attr('id', 'marker')
    //       .attr('x', d3.event.x)
    //       .attr('y', d3.event.y)
    //       .attr('opacity', 0.3);
    //   })
    //   .on('drag', function drag() {
    //     const marker = d3.select(this).select('#marker');
    //     marker.attr('width', () => d3.event.x - parseFloat(marker.style('x').replace(/px/, '')));
    //     marker.attr('height', () => d3.event.y - parseFloat(marker.style('y').replace(/px/, '')));
    //   })
    //   .on('end', function dragEnd() {
    //     d3.select(this)
    //       .select('#marker')
    //       .remove();
    //   }));

    svg
      .append('svg:image')
      .attr('width', viewBoxWidth)
      .attr('height', viewBoxHeight)
      .attr('xlink:href', this.props.imageUrl)
      .on('click', that.unselectAll);

    const machineGroup = svg.selectAll('g')
      .data(machines)
      .enter()
      .append('g')
      .attr('class', 'hover')
      .call(d3.drag()
        .on('start', function dragStart() {
          if (that.state.edit) {
            d3.select(this).raise().classed('active', true);
          }
        })
        .on('drag', function drag() {
          if (that.state.edit) {
            d3.select(this)
              .select('rect')
              .attr('x', (d) => {
                const data = d;
                data.x = d3.event.x;
                return data.x;
              })
              .attr('y', (d) => {
                const data = d;
                data.y = d3.event.y;
                return data.y;
              });
            d3.select(this)
              .select('text')
              .attr('x', (d) => {
                const data = d;
                data.x = d3.event.x;
                return data.x + textMarginX;
              })
              .attr('y', (d) => {
                const data = d;
                data.y = d3.event.y;
                return data.y + textMarginY;
              });
            d3.select(this)
              .select('circle')
              .attr('cx', (d) => {
                const data = d;
                data.cx = d3.event.x;
                return data.cx + circleMarginX;
              })
              .attr('cy', (d) => {
                const data = d;
                data.cy = d3.event.y;
                return data.cy + circleMarginY;
              });
            d3.select(this)
              .select('#circleText')
              .attr('x', (d) => {
                const data = d;
                data.x = d3.event.x;
                return data.x + circleTextMarginX;
              })
              .attr('y', (d) => {
                const data = d;
                data.y = d3.event.y;
                return data.y + circleTextMarginY;
              });
          }
        })
        .on('end', function dragEnd(d) {
          if (that.state.edit) {
            positions[d.shortName].x = d.x;
            positions[d.shortName].y = d.y;
            d3.select(this)
              .classed('active', false);
          }
        }))
      .on('click', function onCLick(d) {
        if (that.state.selected.includes(d.shortName)) {
          d3.select(this).select('rect')
            .attr('stroke', 'none');
        } else {
          d3.select(this).select('rect')
            .attr('stroke', '#1d7aca')
            .attr('stroke-width', 3);
        }
        that.handleSelected(d);
      });

    machineGroup
      .append('rect')
      .attr('width', rectWidth)
      .attr('height', rectHeight)
      .attr('x', d => positions[d.shortName].x)
      .attr('y', d => positions[d.shortName].y)
      .attr('fill', (d) => {
        switch (d.currentState) {
          case 'red':
            return '#e24d42';
          case 'yellow':
            return '#e0752d';
          case 'green':
            return '#508642';
          default:
            return 'grey';
        }
      });

    machineGroup
      .append('text')
      .attr('x', d => positions[d.shortName].x + textMarginX)
      .attr('y', d => positions[d.shortName].y + textMarginY)
      .text(d => d.shortName)
      .attr('font-size', '14px')
      .attr('fill', 'white')
      .attr('text-anchor', 'middle');


    machineGroup
      .filter(d => (
        alarms.allIds.includes(d.alarmId)
        && alarmsConfig.allIds.filter(id => alarmsConfig.byId[id].machineId === d.id).length > 0
      ))
      .append('circle')
      .attr('cx', d => positions[d.shortName].x + circleMarginX)
      .attr('cy', d => positions[d.shortName].y + circleMarginY)
      .attr('r', 11)
      .attr('fill', (d) => {
        switch (d.currentState) {
          case 'red':
            return '#e24d42';
          case 'yellow':
            return '#e0752d';
          // case 'green':
          //   return '#508642';
          default:
            return 'grey';
        }
      });

    machineGroup
      .filter(d => alarms.allIds.includes(d.alarmId))
      .append('text')
      .attr('fill', 'white')
      .attr('id', 'circleText')
      .attr('x', d => positions[d.shortName].x + circleTextMarginX)
      .attr('y', d => positions[d.shortName].y + circleTextMarginY)
      .text((d) => {
        const start = moment(alarms.byId[d.alarmId].start);
        const timeActive = timeNow.diff(start);
        const durationInMins = Math.ceil(moment.duration(timeActive).asMinutes());
        const alarmConfigId = alarmsConfig.allIds.filter(id => alarmsConfig.byId[id].machineId === d.id);
        if (alarmConfigId.length > 0) {
          const alarmConfig = alarmsConfig.byId[alarmConfigId[0]];
          const keyName = d.currentState === 'red' ? 'redConfig' : 'yellowConfig';
          for (let i = 0; i < 3; i++) {
            if (durationInMins < parseInt(alarmConfig[keyName][i].value, 10)) {
              return (i + 1).toString();
            }
            if (i === 2) {
              return '3';
            }
          }
        }
        return '';
      })
      .attr('font-size', '12px');

    this.svg = svg;
    this.machineGroup = machineGroup;
    this.positions = positions;
    this.machines = machines;
  }

  modifyEdit() {
    if (this.state.edit) {
      this.setState({ saveIsPending: true });
      const { currentOrganisation, hall } = this.props;
      const body = {
        data: {
          type: 'halls',
          attributes: {
            thingsLocation: this.positions,
          },
        },
      };
      update(`${apiEndpoint}/organisations/${currentOrganisation}/halls/${hall.id}`, body)
        .then(() => this.setState({ saveIsPending: false }));
      // TODO: Handle catch
    }
    this.setState(prevState => ({ edit: !prevState.edit }));
  }

  handleSelected(machine) {
    this.setState((prevState) => {
      if (!prevState.selected.includes(machine.shortName)) {
        return { selected: [...prevState.selected, machine.shortName] };
      }
      return { selected: prevState.selected.filter(name => name !== machine.shortName) };
    });
  }

  unselectAll() {
    this.setState({ selected: [] });
    this.machineGroup.selectAll('rect')
      .attr('stroke', 'none');
  }

  openMetrics() {
    const machines = this.machines
      .filter(({ shortName }) => this.state.selected.includes(shortName))
      .map(({ shortName, id }) => ({
        shortName,
        id,
      }));
    localSet('Metrics/SelectedMachines', machines);
    this.setState({ moveToMetrics: true });
  }

  render() {
    const {
      edit, selected, saveIsPending, moveToMetrics,
    } = this.state;
    const { t } = this.props;
    return (
      <div>
        {moveToMetrics
          ? (
            <Redirect to="/metrics" />
          )
          : (
            <div>
              <Button
                icon
                floated="right"
                primary={!edit}
                positive={edit}
                labelPosition="left"
                loading={saveIsPending}
                onClick={this.modifyEdit}
              >
                <Icon name={edit ? 'save' : 'edit'} />
                {edit ? t('common.save') : t('common.edit')}
              </Button>
              <Button
                icon
                floated="right"
                primary
                labelPosition="left"
                disabled={selected.length === 0}
                onClick={this.openMetrics}
              >
                <Icon name="eye" />
                {t('common.inspect')}
              </Button>
              <div style={{ padding: '0.5em' }}>
                <AppInfo
                  info={[
                    t('info.select_machines'),
                    t('info.edit_machines'),
                  ]}
                  style={{ float: 'right' }}
                />
              </div>
              <div id="floorPlan" />
            </div>
          )
        }
      </div>
    );
  }
}

export default translate()(FloorPlan);



// WEBPACK FOOTER //
// ./src/components/Overview/FloorPlan.js